import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { SearchIcon, ArrowLeftIcon, ArrowRightIcon, DownloadIcon } from "../assets/dashboard-icon.svg";
import { useRef, useState, useEffect } from "react";
import { formatAmount, formatDate, getStatusBadgeClass, getStatusForSalaryStatus, mapInvoiceStatus } from "../helper/common.helper";
import { useSalaries } from "../hooks/useSalaries";
import { SalaryRecord } from "../api/salary";
import { downloadSalarySlipAsPdf } from "../../common/invoiceDownloadHelper";
import LoadingCard from '../components/SkeletonLoading'
import { InvoiceTableSkeleton } from "./dashboard/manage-invoice-skeleton.component";
import { EmptyState } from "./dashboard/EmptyState";

export const SalaryNoticeSection = ({
    className = '',
}: {
    className?: string
}
) => {
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;

    // First-mount shimmer skeleton (~1s) — page chrome stays visible.
    const [showSkeleton, setShowSkeleton] = useState(true);
    useEffect(() => {
        const timer = setTimeout(() => setShowSkeleton(false), 1000);
        return () => clearTimeout(timer);
    }, []);
    const PAGE_SIZE = 10
    const {
        loading,
        error,
        currentPage,
        totalPages,
        totalCount,
        searchQuery,
        salaries,
        setCurrentPage,
        setSearchQuery,
        goToNextPage,
        goToPreviousPage,
    } = useSalaries(PAGE_SIZE);
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };


        if (showSkeleton) {
            return (
                <div className={`invoice-table-container sn-skeleton-host ${className}`}>
                    <InvoiceTableSkeleton columns={9} filters="search" />
                </div>
            );
        }

        return (
        <div className={`invoice-table-container ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">
                    <span className="sn-section-head">
                        <span className="sn-section-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                                <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                                <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
                            </svg>
                        </span>
                        {t('MY_SALARIES')}
                    </span>
                </h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('HEADER_SEARCH') || 'Search'}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />
                    </div>
                </div>
            </div>


            <div className="invoice-table-content">
                

                {/* Table */}
                <div className="salary-table-scroll-wrapper">
                    <div className="invoice-table">
                        
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('SERIAL_ID')}</TableColumn>
                                <TableColumn>{t('CLIENT_NAME')}</TableColumn>
                                <TableColumn>{t('AMOUNT')}</TableColumn>
                                <TableColumn>{t('SALARY_TYPE')}</TableColumn>
                                <TableColumn>{t('INVOICE_NO')}</TableColumn>
                                <TableColumn>{t('CREATED_DATE')}</TableColumn>
                                <TableColumn>{t('INVOICE_STATUS')}</TableColumn>
                                <TableColumn>{t('SALARY_STATUS')}</TableColumn>
                                <TableColumn>{t('ACTION_LABEL')}</TableColumn>
                            </TableHeader>
                        )}
                        
                        
                        <TableBody>
                            {isMobile ? (
                                // Mobile: Card-based layout
                                salaries?.map((salary: SalaryRecord, index: number) => (
                                    <MobileCardWrapper key={salary.id || salary.id}>
                                        <MobileCardRow label={t('ID')}>
                                            <span className="invoice-table-detailed__invoice-link">{(currentPage - 1) * PAGE_SIZE + index + 1}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CLIENT_NAME')}>
                                            {salary.clientName || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('AMOUNT')}>
                                            {formatAmount(Number(salary.amount)) || 'N/A'} {t('kr')}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('SALARY_TYPE')}>
                                            {salary.salaryType === 1
                                                ? t("INVOICE")
                                                : salary.salaryType === 2   
                                                    ? t("HOLIDAY_PAYOUT")
                                                    : t("MANUAL_SALARY")}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('INVOICE_NO')}>
                                            {salary?.invoiceNumber || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CREATED_DATE')}>
                                            {formatDate(salary.createdDate) || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('INVOICE_STATUS')}>
                                            <span className={getStatusBadgeClass(salary?.invoiceStatus || 0)}>
                                                {t(mapInvoiceStatus(salary?.invoiceStatus)) || '-'}
                                            </span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('SALARY_STATUS')}>
                                            <span className={getStatusBadgeClass(salary?.salaryStatus || 0)}>
                                                {salary.salaryStatus === 1 ? t('PAID') : t('UNPAID') || 'N/A'}
                                            </span>
                                        </MobileCardRow>

                                        <MobileCardRow label={t('ACTION_LABEL')}>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("DOWNLOAD")}
                                                    data-tip={t("DOWNLOAD")}
                                                    aria-label={t("DOWNLOAD")}
                                                    onClick={() => {
                                                        const identifier =
                                                            salary?.salaryType === 3
                                                                ? salary?.id || salary?.id
                                                                : salary?.invoiceNumber;

                                                        downloadSalarySlipAsPdf(identifier, 'sv-se', salary.salaryType,);
                                                    }}
                                                >
                                                    <DownloadIcon />
                                                </button>
                                            </div>
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                salaries.map((salary: SalaryRecord, index: number) => (
                                    <TableRow key={salary.id}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{(currentPage - 1) * PAGE_SIZE + index + 1}</span>
                                        </TableCell>
                                        <TableCell><span className="sn-link">{salary?.clientName || 'N/A'}</span></TableCell>
                                        <TableCell><span className="sn-amount">{formatAmount(Number(salary.amount)) || 'N/A'}{t('kr')}</span></TableCell>
                                        <TableCell>{
                                            salary.salaryType === 1
                                                ? t("INVOICE")
                                                : salary.salaryType === 2
                                                    ? t("HOLIDAY_PAYOUT")
                                                    : t("MANUAL_SALARY")

                                                    || '-'}</TableCell>
                                        <TableCell><span className="sn-link">{salary?.invoiceNumber || '-'}</span></TableCell>
                                        <TableCell>{formatDate(salary.createdDate) || '-'}</TableCell>
                                        <TableCell>
                                            <span className={getStatusBadgeClass(salary?.invoiceStatus || 0)}>
                                                {t(mapInvoiceStatus(salary?.invoiceStatus)) || '-'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={getStatusForSalaryStatus(salary.salaryStatus)}>
                                                {salary.salaryStatus === 1 ? t('PAID') : t('UNPAID')}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={t("DOWNLOAD")}
                                                    data-tip={t("DOWNLOAD")}
                                                    aria-label={t("DOWNLOAD")}
                                                    onClick={() => {
                                                        const identifier =
                                                            salary?.salaryType === 3
                                                                ? salary?.id || salary?.id
                                                                : salary?.invoiceNumber;

                                                        downloadSalarySlipAsPdf(identifier, 'sv-se', salary.salaryType,);
                                                    }}
                                                >
                                                    <DownloadIcon />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>
                </div>

                {/* Loading / empty state — full width, outside the display:table */}
                {loading && salaries.length === 0 && (
                    <LoadingCard />
                )}
                {!loading && salaries.length === 0 && (
                    <EmptyState
                        title={t('NO_SALARIES_FOUND')}
                        subtitle={error ? String(error) : t('NO_DATA_SUBTITLE')}
                    />
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP') || 'Prev'}</span>
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}